import React, { FC, useState, type PropsWithChildren } from "react";
import AccordionSection from "./AccordionSection";
import { Switch } from "../../../../../components";
import type { ProfileLinkConfig } from "../../../../../client";

const SettingRow: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="flex items-center justify-between gap-2">{children}</div>
  );
};

const SettingText: FC<PropsWithChildren> = ({ children }) => {
  return <p className="text-gray-9 text-sm font-medium grow">{children}</p>;
};

const SectionSubtitle: FC<PropsWithChildren> = ({ children }) => {
  return (
    <p className="text-sm font-medium text-gray-9 mt-2 mb-1">{children}</p>
  );
};

interface PDFSettingsLinkSettingsProps {
  disabled?: boolean;
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
  linkId: string;
  organizationId: string;
  onPosthogCapture?: (event: string, properties: any) => void;
}

const PDFSettingsLinkSettings: FC<PDFSettingsLinkSettingsProps> = ({
  disabled,
  linkConfig,
  onUpdate,
  linkId,
  organizationId,
  onPosthogCapture,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const captureEvent = (event: string, value: any) => {
    if (onPosthogCapture && localStorage.getItem("current-org-id") !== null) {
      onPosthogCapture(event, {
        profileLinkId: linkId,
        organizationId: organizationId,
        value: value,
      });
    }
  };

  return (
    <AccordionSection
      title="PDF Settings"
      subtitle={(() => {
        const activeSettings: string[] = [];
        if (linkConfig?.brandingAndContactConfig?.displayQRLink ?? true) {
          activeSettings.push("QR code");
        } else {
          activeSettings.push("No QR code");
        }
        if (linkConfig?.brandingAndContactConfig?.displayTopLogo ?? true) {
          activeSettings.push("top logo");
        }
        if (linkConfig?.brandingAndContactConfig?.displayTopContact ?? true) {
          activeSettings.push("top contact");
        }
        if (linkConfig?.brandingAndContactConfig?.displayBottomLogo) {
          activeSettings.push("bottom logo");
        }
        if (linkConfig?.brandingAndContactConfig?.displayBottomContact) {
          activeSettings.push("bottom contact");
        }
        if (activeSettings.length === 0) {
          return "No settings active";
        }
        return activeSettings.join(", ");
      })()}
      expanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      <SettingRow>
        <Switch
          checked={linkConfig?.brandingAndContactConfig?.displayQRLink ?? true}
          disabled={disabled}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            captureEvent("LINK_EDITOR_PDF_DISPLAY_QR_LINK", checked);

            if (!linkConfig?.brandingAndContactConfig) {
              onUpdate({
                brandingAndContactConfig: {
                  displayContactInformation: true,
                  contactInformationPlacement: "top",
                  displayQRLink: checked,
                  displayLogo: true,
                  displayTopContact: true,
                  displayBottomContact: false,
                  displayTopLogo: true,
                  displayBottomLogo: false,
                },
              });
            } else {
              onUpdate({
                brandingAndContactConfig: {
                  ...linkConfig.brandingAndContactConfig,
                  displayQRLink: checked,
                },
              });
            }
          }}
        />
        <SettingText>Display QR code</SettingText>
      </SettingRow>

      <SectionSubtitle>Top section</SectionSubtitle>

      <SettingRow>
        <Switch
          checked={linkConfig?.brandingAndContactConfig?.displayTopLogo ?? true}
          disabled={disabled}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            captureEvent("LINK_EDITOR_PDF_DISPLAY_TOP_LOGO", checked);

            if (!linkConfig?.brandingAndContactConfig) {
              onUpdate({
                brandingAndContactConfig: {
                  displayContactInformation: checked,
                  contactInformationPlacement: "top",
                  displayQRLink: true,
                  displayLogo: true,
                  displayTopContact: true,
                  displayBottomContact: false,
                  displayTopLogo: checked,
                  displayBottomLogo: false,
                },
              });
            } else {
              onUpdate({
                brandingAndContactConfig: {
                  ...linkConfig.brandingAndContactConfig,
                  displayTopLogo: checked,
                },
              });
            }
          }}
        />
        <SettingText>Display logo</SettingText>
      </SettingRow>

      <SettingRow>
        <Switch
          checked={
            linkConfig?.brandingAndContactConfig?.displayTopContact ?? true
          }
          disabled={false}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            captureEvent(
              "LINK_EDITOR_PDF_TOP_CONTACT",
              checked ? "top" : "bottom",
            );

            if (!linkConfig?.brandingAndContactConfig) {
              onUpdate({
                brandingAndContactConfig: {
                  displayContactInformation: checked,
                  contactInformationPlacement: "top",
                  displayQRLink: true,
                  displayLogo: true,
                  displayTopContact: checked,
                  displayBottomContact: false,
                  displayTopLogo: true,
                  displayBottomLogo: false,
                },
              });
            } else {
              onUpdate({
                brandingAndContactConfig: {
                  ...linkConfig.brandingAndContactConfig,
                  displayTopContact: checked,
                },
              });
            }
          }}
        />
        <SettingText>Display contact</SettingText>
      </SettingRow>

      <SectionSubtitle>Bottom section</SectionSubtitle>

      <SettingRow>
        <Switch
          checked={
            linkConfig?.brandingAndContactConfig?.displayBottomLogo ?? false
          }
          disabled={disabled}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            captureEvent("LINK_EDITOR_PDF_DISPLAY_BOTTOM_LOGO", checked);

            if (!linkConfig?.brandingAndContactConfig) {
              onUpdate({
                brandingAndContactConfig: {
                  displayContactInformation: checked,
                  contactInformationPlacement: "top",
                  displayQRLink: true,
                  displayLogo: true,
                  displayTopContact: true,
                  displayBottomContact: false,
                  displayTopLogo: true,
                  displayBottomLogo: checked,
                },
              });
            } else {
              onUpdate({
                brandingAndContactConfig: {
                  ...linkConfig.brandingAndContactConfig,
                  displayBottomLogo: checked,
                },
              });
            }
          }}
        />
        <SettingText>Display logo</SettingText>
      </SettingRow>

      <SettingRow>
        <Switch
          checked={
            linkConfig?.brandingAndContactConfig?.displayBottomContact ?? false
          }
          disabled={false}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            captureEvent(
              "LINK_EDITOR_PDF_BOTTOM_CONTACT",
              checked ? "top" : "bottom",
            );

            if (!linkConfig?.brandingAndContactConfig) {
              onUpdate({
                brandingAndContactConfig: {
                  displayContactInformation: checked,
                  contactInformationPlacement: "top",
                  displayQRLink: true,
                  displayLogo: true,
                  displayTopContact: true,
                  displayBottomContact: checked,
                  displayTopLogo: true,
                  displayBottomLogo: false,
                },
              });
            } else {
              onUpdate({
                brandingAndContactConfig: {
                  ...linkConfig.brandingAndContactConfig,
                  displayBottomContact: checked,
                },
              });
            }
          }}
        />
        <SettingText>Display contact</SettingText>
      </SettingRow>
    </AccordionSection>
  );
};

export default PDFSettingsLinkSettings;
